import React, { useState } from 'react';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const PaymentForm = ({ clientSecret, onSuccess, onCreatePaymentIntent, amount, bookingInfo, trailerData }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [billingInfo, setBillingInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    addressLine1: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'US'
  });

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setBillingInfo(prev => ({ ...prev, [name]: value }));
  };

  const handleCreatePaymentIntent = async (e) => {
    e?.preventDefault();
    setError(null);

    if (!billingInfo?.firstName || !billingInfo?.lastName || !billingInfo?.email || !billingInfo?.addressLine1 || !billingInfo?.city || !billingInfo?.state || !billingInfo?.postalCode) {
      setError('Please fill in all required billing information');
      return;
    }

    setIsProcessing(true);

    try {
      const mockClientSecret = `pi_mock_${Date.now()}_secret_${Math.random()?.toString(36)?.substr(2, 9)}`;
      
      setTimeout(() => {
        onCreatePaymentIntent(mockClientSecret);
        setIsProcessing(false);
      }, 1000);
    } catch (err) {
      setError('Failed to initialize payment. Please try again.');
      setIsProcessing(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const { error: submitError, paymentIntent } = await stripe?.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location?.origin}/booking`,
        },
        redirect: 'if_required'
      });

      if (submitError) {
        setError(submitError?.message || 'Payment failed. Please try again.');
        setIsProcessing(false);
      } else if (paymentIntent?.status === 'succeeded') {
        onSuccess(paymentIntent);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      setIsProcessing(false);
    }
  };

  if (!clientSecret) {
    return (
      <form onSubmit={handleCreatePaymentIntent} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="First Name"
            name="firstName"
            value={billingInfo?.firstName}
            onChange={handleInputChange}
            required
            placeholder="John"
          />
          <Input
            label="Last Name"
            name="lastName"
            value={billingInfo?.lastName}
            onChange={handleInputChange}
            required
            placeholder="Doe"
          />
        </div>
        <Input
          label="Email"
          name="email"
          type="email"
          value={billingInfo?.email}
          onChange={handleInputChange}
          required
          placeholder="john.doe@example.com"
        />
        <Input
          label="Phone Number"
          name="phone"
          type="tel"
          value={billingInfo?.phone}
          onChange={handleInputChange}
          placeholder="(555) 123-4567"
        />
        <div className="pt-4 border-t border-border">
          <h3 className="font-heading text-lg font-semibold text-foreground mb-4">
            Billing Address
          </h3>

          <div className="space-y-4">
            <Input
              label="Address Line 1"
              name="addressLine1"
              value={billingInfo?.addressLine1}
              onChange={handleInputChange}
              required
              placeholder="123 Main Street"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="City"
                name="city"
                value={billingInfo?.city}
                onChange={handleInputChange}
                required
                placeholder="Austin"
              />
              <Input
                label="State"
                name="state"
                value={billingInfo?.state}
                onChange={handleInputChange}
                required
                placeholder="TX"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Postal Code"
                name="postalCode"
                value={billingInfo?.postalCode}
                onChange={handleInputChange}
                required
                placeholder="78701"
              />
              <Input
                label="Country"
                name="country"
                value={billingInfo?.country}
                onChange={handleInputChange}
                required
                placeholder="US"
              />
            </div>
          </div>
        </div>
        {error && (
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
            <div className="flex items-start space-x-2">
              <Icon name="AlertCircle" size={18} color="var(--color-destructive)" strokeWidth={2} />
              <p className="font-body text-sm text-destructive">{error}</p>
            </div>
          </div>
        )}
        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          loading={isProcessing}
          iconName="ArrowRight"
          iconPosition="right"
        >
          Continue to Payment
        </Button>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <PaymentElement />

      {error && (
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <div className="flex items-start space-x-2">
            <Icon name="AlertCircle" size={18} color="var(--color-destructive)" strokeWidth={2} />
            <p className="font-body text-sm text-destructive">{error}</p>
          </div>
        </div>
      )}

      <div className="flex items-start space-x-2 p-4 bg-muted rounded-md">
        <Icon name="Info" size={18} color="var(--color-primary)" strokeWidth={2} />
        <p className="font-body text-sm text-muted-foreground">
          Your payment information is securely processed by Stripe. We never store your card details.
        </p>
      </div>

      <Button
        type="submit"
        variant="default"
        size="lg"
        fullWidth
        loading={isProcessing}
        disabled={!stripe || isProcessing}
        iconName="Lock"
        iconPosition="left"
      >
        {isProcessing ? 'Processing Payment...' : `Pay $${amount?.toFixed(2)}`}
      </Button>
    </form>
  );
};

export default PaymentForm;